import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchConsoleAdmins, inviteConsoleAdmin, revokeConsoleAdmin, ConsoleAdmin } from '../services/consoleApi';

/* PEAR-only page: manage who can access the admin console. Invite-only — there is
   no public signup. Uses the same email-invite flow as the rest of the product. */
const ConsoleAdmins: React.FC = () => {
  const { currentUser } = useAuth();
  const [admins, setAdmins] = useState<ConsoleAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchConsoleAdmins();
    if (r.ok) setAdmins(r.admins);
    else setMsg({ kind: 'err', text: r.error || 'Could not load admins.' });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    const email = inviteEmail.trim();
    if (!email) return;
    setBusy(true);
    const r = await inviteConsoleAdmin(email);
    setBusy(false);
    if (r.ok) {
      setMsg({ kind: 'ok', text: r.alreadyAdmin ? `${email} is already an admin.` : `Invite sent to ${email}.` });
      setInviteEmail('');
      load();
    } else {
      setMsg({ kind: 'err', text: r.error || 'Could not send the invite.' });
    }
  };

  const onRevoke = async (a: ConsoleAdmin) => {
    if (!window.confirm(`Remove console access for ${a.email}? They keep any per-school (fleet admin) access.`)) return;
    setMsg(null);
    setBusy(true);
    const r = await revokeConsoleAdmin(a.uid);
    setBusy(false);
    if (r.ok) { setMsg({ kind: 'ok', text: `Removed ${a.email}.` }); load(); }
    else setMsg({ kind: 'err', text: r.error || 'Could not remove that admin.' });
  };

  const wrap: React.CSSProperties = { maxWidth: 720, margin: '32px auto', padding: '0 20px', fontFamily: '-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif', color: '#111827' };
  const card: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, marginBottom: 18 };
  const input: React.CSSProperties = { flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 9, fontSize: 14 };
  const btn: React.CSSProperties = { padding: '10px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 };

  return (
    <div style={wrap}>
      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>Console admins</h1>
      <p style={{ fontSize: 13.5, color: '#6b7280', margin: '0 0 20px' }}>
        People who can log into this PEAR console. It's invite-only — an invite emails a secure link;
        they set a password and they're in.
      </p>

      <div style={card}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Invite a new admin</div>
        <form onSubmit={onInvite} style={{ display: 'flex', gap: 10 }}>
          <input style={input} type="email" placeholder="name@example.com" value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)} />
          <button style={btn} type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button>
        </form>
        {msg && (
          <div style={{ marginTop: 12, fontSize: 13, color: msg.kind === 'ok' ? '#15803d' : '#b91c1c' }}>{msg.text}</div>
        )}
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Current admins ({admins.length})</div>
        {loading ? (
          <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>
        ) : (
          <div>
            {admins.map((a) => (
              <div key={a.uid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {a.email}
                    {a.grandfather && <span style={{ marginLeft: 8, fontSize: 11, background: '#eef2ff', color: '#4338ca', padding: '2px 7px', borderRadius: 999 }}>owner</span>}
                    {currentUser && a.uid === currentUser.uid && <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280' }}>(you)</span>}
                  </div>
                  {a.invitedBy && <div style={{ fontSize: 12, color: '#9ca3af' }}>invited by {a.invitedBy}</div>}
                </div>
                {currentUser && a.uid !== currentUser.uid && (
                  <button onClick={() => onRevoke(a)} disabled={busy}
                    style={{ padding: '6px 12px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsoleAdmins;
